import { expandRecurring } from "../components/Calendar/recurrence";
import {lineupProjection} from "./matchLineup";

export const EVENT_TAGS = ["Scrim", "Game Day", "Premier", "Vod Review", "Ranked", "Dry Runs", "Learning from THE MANUAL", "Coaching Session", "Watch Party", "Tentative Session", "Other"];
export const SCRIM_TYPES = ['Fundamental', 'Strategy Scrims', 'Performance Scrims'];
export const eventTagLabel = event => event.tag === 'Scrim' && SCRIM_TYPES.includes(event.scrimType) ? `Scrim · ${event.scrimType}` : event.tag || 'Other';
export const MATCH_FORMATS = ["bo1", "bo3", "bo5", "bo7"];
export const newSetupId = () => crypto.randomUUID();
export const hasSetupEventId = event => typeof event?.eventId === "string" && event.eventId.trim().length > 0;
// Calendar planning may leave maps undecided; media must have a real destination.
export const hasSetupMap = slot => {
  const assigned = value => typeof value === "string" && !!value.trim()
    && !/^(unknown|map\s*tbd|tbd|none|not\s*set)$/i.test(value.trim());
  return assigned(slot?.mapId) && (!slot?.mapName || assigned(slot.mapName));
};

// Merge occupied intervals so overlapping rows never produce artificial gaps.
export function eventBlockGaps(startUTC, endUTC, events) {
  const start = Date.parse(startUTC), end = Date.parse(endUTC);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return [];
  const occupied = events.map(event => [Math.max(start, Date.parse(event.startUTC)), Math.min(end, Date.parse(event.endUTC))])
    .filter(([from, to]) => Number.isFinite(from) && Number.isFinite(to) && to > from)
    .sort((a, b) => a[0] - b[0]);
  const gaps = [];
  let cursor = start;
  for (const [from, to] of occupied) {
    if (from > cursor) gaps.push({startUTC: new Date(cursor).toISOString(), endUTC: new Date(from).toISOString()});
    cursor = Math.max(cursor, to);
  }
  if (cursor < end) gaps.push({startUTC: new Date(cursor).toISOString(), endUTC: new Date(end).toISOString()});
  return gaps;
}

// Links identify an occurrence and a numbered map, never a display title.
export function setupKey(event, slotId = "map-1", scope = "team") {
  if (!hasSetupEventId(event)) return "";
  return ["setup", scope, encodeURIComponent(event.eventId), event.instanceDate || "-", slotId].join(":");
}
export function parseSetupKey(key = "") {
  if (typeof key !== "string") return null;
  const parts = key.split(":");
  if (parts[0] !== "setup" || parts.length !== 5 || !["team", "personal"].includes(parts[1]) || !/^map-[1-7]$/.test(parts[4])) return null;
  try {return {scope: parts[1], eventId: decodeURIComponent(parts[2]), instanceDate: parts[3] === "-" ? null : parts[3], slotId: parts[4]};}
  catch (_) {return null;}
}
export function eventSlots(event = {}) {
  const existing = Array.isArray(event.mapSlots) && event.mapSlots.length ? event.mapSlots : [{
    id: "map-1", mapId: event.mapId || "", mapName: event.mapName || "", matchId: event.matchId || "",
    statsId: event.teamStatsMatchId || "", titleOverride: "",
  }];
  const count = Array.isArray(event.mapSlots) && event.mapSlots.length ? existing.length
    : MATCH_FORMATS.includes(event.matchFormat) ? Math.floor(Number(event.matchFormat.slice(2)) / 2) + 1 : 1;
  return Array.from({length: Math.max(count, existing.length)}, (_, index) => ({
    id: `map-${index + 1}`, mapId: "", mapName: "", matchId: "", statsId: "", titleOverride: "", ...existing[index],
  }));
}
export function automaticMatchTitle(event, slot = {}) {
  const opponent = ["scrim", "game day"].includes(String(event.tag).toLowerCase())
    ? `Vs ${String(event.opponentName || "").trim() || "Unknown opponent"}` : "";
  return [String(event.competition || "").trim(), eventTagLabel(event), opponent, String(slot.mapName || "").trim() || "Map TBD"].filter(Boolean).join(" | ");
}
export function matchTitle(event, slot) {
  return String(slot?.titleOverride || "").trim() || automaticMatchTitle(event, slot);
}
export function flattenSetupEvent(event, slot, scope = "team") {
  return {...event, ...slot, ...lineupProjection(slot), eventId: setupKey(event, slot.id, scope), sourceEventId: event.eventId,
    slotId: slot.id, calendar: scope, note: matchTitle(event, slot), title: matchTitle(event, slot)};
}
export function resolveSetupEvent(events, key) {
  const identity = parseSetupKey(key);
  if (!identity) return (events || []).find(event => event.eventId === key) || null;
  const source = (events || []).find(event => event.eventId === identity.eventId);
  if (!source) return null;
  if (!identity.instanceDate) return source;
  const from = new Date(`${identity.instanceDate}T00:00:00Z`);
  const override = source.overrides?.[identity.instanceDate];
  // Include both the original date and a moved occurrence's date.
  const start = new Date(Math.min(+from - 86400000, Date.parse(override?.startUTC) || +from));
  const end = new Date(Math.max(+from + 172800000, Date.parse(override?.endUTC) || +from));
  return expandRecurring([source], start, end).find(event => event.instanceDate === identity.instanceDate) || null;
}
export function replaceSetupEvent(events, selected, updated) {
  if (!selected.instanceDate) return events.map(event => event.eventId === selected.eventId ? updated : event);
  const {overrides, exceptions, instanceDate, isRecurringInstance, __teamId, ...override} = updated;
  return events.map(event => event.eventId !== selected.eventId ? event : {...event, overrides: {
    ...event.overrides, [selected.instanceDate]: {...event.overrides?.[selected.instanceDate], ...override},
  }});
}
export function setupRevision(event) {
  const ordered = value => Array.isArray(value) ? value.map(ordered) : value && typeof value === "object"
    ? Object.fromEntries(Object.keys(value).sort().filter(key => value[key] !== undefined).map(key => [key, ordered(value[key])])) : value;
  const instant = value => Number.isFinite(Date.parse(value)) ? new Date(value).toISOString() : value;
  return JSON.stringify(ordered([instant(event?.startUTC), instant(event?.endUTC), event?.tag, event?.scrimType || '', event?.competition, event?.opponentName,
    event?.matchFormat, event?.mapSlots, event?.mapId, event?.mapName, event?.matchId, event?.note,
    event?.repeat || "none", event?.repeatEndDate || null, event?.participantsUid, event?.participants, event?.reminder || ""]));
}
export const eventMapLimit = event => MATCH_FORMATS.includes(event.matchFormat) ? Number(event.matchFormat.slice(2)) : 7;

export function validateEventSetup(event, previous) {
  if (event.tag === 'Scrim' && event.scrimType && !SCRIM_TYPES.includes(event.scrimType)) throw new Error('Choose a valid Scrim sub-option.');
  if (!EVENT_TAGS.includes(event.tag) || !event.startUTC || !event.endUTC || !Number.isFinite(Date.parse(event.startUTC)) || !(Date.parse(event.endUTC) > Date.parse(event.startUTC))) {
    throw new Error("Choose an event tag and a valid start and end time.");
  }
  const slots = eventSlots(event);
  if (slots.length > 7) throw new Error("An event can contain at most seven maps.");
  const maximum = eventMapLimit(event);
  if (slots.length > maximum) throw new Error("Remove unused map slots before reducing the match format.");
  for (const old of eventSlots(previous || {})) {
    if (!old.matchId && !old.statsId && !old.localMatchId) continue;
    const next = slots.find(slot => slot.id === old.id);
    if (!next || ((old.matchId || old.localMatchId) && (next.mapId !== old.mapId || next.mapName !== old.mapName))) throw new Error("This map already has a recording. Keep its map and links, or move that recording first.");
    if (next.matchId !== old.matchId || next.statsId !== old.statsId || next.localMatchId !== old.localMatchId) throw new Error("Linked recordings and stats cannot be removed through event setup.");
  }
  return {...event, scrimType:event.tag === 'Scrim' ? event.scrimType || '' : '', recurrenceId: event.repeat && event.repeat !== "none" ? event.recurrenceId || event.eventId : null, mapSlots: slots};
}

export function projectionStamp(event, slot) {
  return JSON.stringify([event.startUTC, event.endUTC, event.tag || "", event.scrimType || '', event.competition || "", event.opponentName || "", slot.mapId || "", slot.mapName || "", matchTitle(event, slot)]);
}
